import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Lock, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api, apiErrorMessage } from "../lib/api";
import { clearDraft, dayLabel, loadDraft, missingLine, saveDraft, shortDay, tokenFromHash } from "../lib/initialAssessment";
import { TEXT, missing as findMissing, rememberReturn, signatureLine } from "../lib/finalFeedback";

/**
 * The final feedback questionnaire (30.10.2026).
 *
 * `/feedback#t=…` — the personal link from an email or the portal. The token
 * sits after `#`, so it never reaches a server's logs; the page sends it in the
 * body. Answers are kept in the browser until sent, under the invite.
 *
 * `/feedback/<code>` — the trainer's group link (decision C): it opens through
 * the portal, so the platform knows who answered and sends them no email.
 */
export default function Feedback() {
  const { code } = useParams();
  return (
    <div className="min-h-screen bg-[#F5F6F6] flex flex-col" data-testid="feedback-page">
      <SEO title="Chestionar de feedback | FPED" noindex />
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {code ? <GroupLink code={code.toLowerCase()} /> : <Personal />}
      </main>
      <Footer />
    </div>
  );
}

function Card({ children, center = false }) {
  return (
    <div className={`rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8 md:p-10 ${center ? "text-center" : ""}`}>{children}</div>
  );
}

const Spinner = () => <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>;

// ------------------------------------------------------------------ the trainer's link

function GroupLink({ code }) {
  const [phase, setPhase] = useState("loading"); // loading · signin · other · invalid · refused
  const [group, setGroup] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const named = await api.get(`/final-feedback/group/${code}`);
        if (alive) setGroup(named.data.group_name || "");
      } catch {
        if (alive) setPhase("invalid");
        return;
      }
      try {
        const r = await api.post(`/portal/final-feedback/code/${code}`);
        window.location.assign(r.data.url);
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        if (status === 401) setPhase("signin");
        else if (status === 403) setPhase("other");
        else if (status === 409) { setDetail(apiErrorMessage(err, "")); setPhase("refused"); }
        else setPhase("invalid");
      }
    })();
    return () => { alive = false; };
  }, [code]);

  if (phase === "loading") return <Spinner />;
  if (phase === "invalid") return <Invalid />;
  if (phase === "refused") return <Card><p className="text-[15px] text-[#373A45]">{detail}</p></Card>;
  if (phase === "other") {
    return (
      <Card>
        <h1 className="font-display text-2xl font-bold text-[#373A45]">Chestionarul de feedback e pentru cursanții grupei {group}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
          Contul cu care ai intrat nu e în această grupă. Dacă ai fost la curs cu altă adresă, intră cu ea.
        </p>
      </Card>
    );
  }
  return <SignIn code={code} group={group} />;
}

function SignIn({ code, group }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setError("Scrie adresa de email completă."); return; }
    setBusy(true);
    setError("");
    try {
      rememberReturn(`/feedback/${code}`);
      await api.post("/portal/auth/request", { email: email.trim() });
      setSent(true);
    } catch {
      setError("Nu am putut trimite linkul. Încearcă din nou.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <h1 className="font-display text-2xl font-bold text-[#373A45]" data-testid="feedback-signin">
        Chestionarul de feedback al grupei {group}
      </h1>
      {sent ? (
        <p className="mt-5 flex gap-3 rounded-2xl bg-[#F5F6F6] p-4 text-sm text-[#373A45]" data-testid="feedback-signin-sent">
          <Mail size={18} className="mt-0.5 shrink-0 text-[#965EA7]" />
          Dacă adresa e a unui cursant din grupă, ți-am trimis linkul. Verifică emailul, inclusiv folderul Spam.
        </p>
      ) : (
        <form onSubmit={submit} noValidate>
          <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
            Scrie adresa de email cu care te-ai înscris la curs. Îți trimitem un link de intrare și deschizi chestionarul de
            feedback de acolo.
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)}
              aria-label="Adresa de email" className={`${FIELD} sm:flex-1`} data-testid="feedback-email" />
            <button type="submit" disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] px-6 py-3 font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} Trimite linkul
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-[#c0483f]" role="alert">{error}</p>}
        </form>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ the personal link

function Personal() {
  const [token] = useState(() => tokenFromHash(typeof window !== "undefined" ? window.location.hash : ""));
  const [page, setPage] = useState(null); // null loading · {state…} · "invalid"
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setPage("invalid"); return; }
    api.post("/final-feedback/open", { token })
      .then((r) => setPage(r.data))
      .catch(() => setPage("invalid"));
  }, [token]);

  if (page === null) return <Spinner />;
  if (page === "invalid") return <Invalid />;
  if (done) return <Thanks name={done.first_name} text={page.form?.thanks} />;
  if (page.state === "completed") return <Already at={page.answered_at} />;
  if (page.state === "closed") return <Closed deadline={page.deadline} />;
  if (page.state === "open") return <Questionnaire page={page} token={token} onDone={setDone} />;
  return <Invalid />;
}

function Invalid() {
  return (
    <Card>
      <h1 className="font-display text-2xl font-bold text-[#373A45]">Linkul nu mai e valid</h1>
      <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
        Folosește linkul din cel mai recent email primit de la noi sau intră în{" "}
        <a href="/portal" className="font-semibold text-[#7A4986] underline">portalul cursului</a>: găsești chestionarul de
        feedback pe cardul grupei.
      </p>
    </Card>
  );
}

function Thanks({ name, text }) {
  return (
    <Card center>
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#EAF4EE] text-[#2F6B4E]"><CheckCircle2 size={24} /></div>
      <h1 className="mt-5 font-display text-2xl font-bold text-[#373A45]" data-testid="feedback-thanks">
        {name ? `Mulțumim, ${name}!` : "Mulțumim!"}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[#373A45]">
        {text || "Îți mulțumim pentru timpul acordat și pentru feedbackul oferit!"}
      </p>
    </Card>
  );
}

function Already({ at }) {
  return (
    <Card center>
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#EAF4EE] text-[#2F6B4E]"><CheckCircle2 size={24} /></div>
      <h1 className="mt-5 font-display text-2xl font-bold text-[#373A45]" data-testid="feedback-already">
        Ai trimis deja chestionarul de feedback pe {shortDay(at)}. Mulțumim!
      </h1>
    </Card>
  );
}

function Closed({ deadline }) {
  return (
    <Card>
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#F0E7F4] text-[#7A4986]"><Lock size={20} /></div>
      <h1 className="mt-4 font-display text-2xl font-bold text-[#373A45]" data-testid="feedback-closed">
        Chestionarul de feedback s-a închis {dayLabel(deadline)}
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">
        Mulțumim pentru interes! Dacă vrei totuși să ne spui ceva despre curs, scrie-ne la{" "}
        <a href="mailto:contact@fped.org" className="font-semibold text-[#7A4986]">contact@fped.org</a>.
      </p>
    </Card>
  );
}

// ------------------------------------------------------------------ the form

function Questionnaire({ page, token, onDone }) {
  const def = page.form;
  const [answers, setAnswers] = useState(() => loadDraft(page.draft_key) || {});
  const [marked, setMarked] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = useRef({});

  useEffect(() => { saveDraft(page.draft_key, answers); }, [answers, page.draft_key]);

  const set = useCallback((id, value) => {
    setAnswers((a) => ({ ...a, [id]: value }));
    setMarked((m) => {
      const left = m.filter((x) => x !== id);
      if (!left.length) setMessage("");
      return left;
    });
  }, []);

  const mark = (ids, text) => {
    setMarked(ids);
    setMessage(text);
    ids.map((id) => refs.current[id]).find(Boolean)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const gaps = findMissing(def, answers);
    if (gaps.length) { mark(gaps, missingLine(gaps.length)); return; }
    setBusy(true);
    setMessage("");
    try {
      const body = { ...answers, testimonial_consent: Boolean(answers.testimonial?.trim() && answers.testimonial_consent) };
      const r = await api.post("/final-feedback/submit", { token, answers: body });
      clearDraft(page.draft_key);
      window.scrollTo({ top: 0, behavior: "smooth" });
      onDone(r.data);
    } catch (err) {
      const missingIds = err?.response?.data?.missing;
      if (Array.isArray(missingIds) && missingIds.length) mark(missingIds, err.response.data.detail);
      else setMessage(err?.response ? apiErrorMessage(err, TEXT.network) : TEXT.network);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="feedback-form">
      <Card>
        <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight text-[#373A45]" style={{ textWrap: "balance" }}>{def.title}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-[#373A45]">{def.intro}</p>
        <p className="mt-3 text-sm leading-relaxed text-[#6B7280]">{def.anonymous}</p>
        {page.reopened && page.deadline && (
          <p className="mt-4 rounded-2xl bg-[#F0E7F4] px-5 py-3 text-sm font-semibold text-[#7A4986]">
            Ți-am redeschis chestionarul de feedback. Îl poți completa până {dayLabel(page.deadline)}.
          </p>
        )}
      </Card>

      <section className="mt-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8">
        <div className="grid gap-8">
          {def.questions.map((q) => (
            <div key={q.id} ref={(el) => { refs.current[q.id] = el; }} data-testid={`q-${q.id}`}
              className={marked.includes(q.id) ? "rounded-2xl ring-2 ring-[#c0483f]/60 ring-offset-8" : ""}>
              <Question q={q} answers={answers} set={set} signature={page.signature} />
              {marked.includes(q.id) && <p className="mt-2 text-sm text-[#c0483f]">{TEXT.answerThis}</p>}
            </div>
          ))}
        </div>
      </section>

      <div className="mt-6 rounded-3xl border border-[#E5E7EB] bg-white p-6 sm:p-8">
        <p className="text-sm text-[#6B7280]">{TEXT.saved}</p>
        {message && (
          <p className="mt-4 flex items-start gap-2 rounded-xl bg-[#c0483f]/10 px-4 py-3 text-sm text-[#a33a32]" role="alert" data-testid="feedback-message">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {message}
          </p>
        )}
        <button type="submit" disabled={busy} data-testid="feedback-submit"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#965EA7] px-8 py-3 text-base font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60">
          {busy && <Loader2 size={18} className="animate-spin" />} Trimite
        </button>
      </div>
    </form>
  );
}

const FIELD = "w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-[15px] text-[#373A45] outline-none focus:bg-white focus:border-[#965EA7]";

function Label({ q }) {
  return (
    <span className="block text-[15px] font-semibold text-[#373A45]">
      {q.n}. {q.label}{!q.required && <span className="font-normal text-[#6B7280]"> {TEXT.optional}</span>}
    </span>
  );
}

function Question({ q, answers, set, signature }) {
  const value = answers[q.id];
  if (q.type === "long") {
    return (
      <label className="block">
        <Label q={q} />
        <textarea value={value || ""} onChange={(e) => set(q.id, e.target.value)} maxLength={q.max} rows={3}
          className={`${FIELD} mt-2`} />
      </label>
    );
  }
  if (q.type === "testimonial") {
    const written = Boolean((value || "").trim());
    return (
      <div>
        <label className="block">
          <Label q={q} />
          <textarea value={value || ""} onChange={(e) => set(q.id, e.target.value)} maxLength={q.max} rows={3}
            className={`${FIELD} mt-2`} data-testid="feedback-testimonial" />
        </label>
        {written && (
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-[#E5E7EB] px-4 py-3 text-sm text-[#373A45]">
            <input type="checkbox" checked={Boolean(answers.testimonial_consent)} data-testid="feedback-consent"
              onChange={(e) => set("testimonial_consent", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#965EA7]" />
            <span>
              {q.consent}
              {signatureLine(signature) && <span className="mt-1 block text-[#6B7280]">De exemplu: „{signatureLine(signature)}”.</span>}
              <span className="mt-1 block text-xs text-[#6B7280]">{q.consent_note}</span>
            </span>
          </label>
        )}
      </div>
    );
  }
  if (q.type === "single") {
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {q.options.map((o) => (
            <label key={o.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-[15px] text-[#373A45] focus-within:ring-2 focus-within:ring-[#965EA7]
              ${value === o.key ? "border-[#965EA7] bg-[#F0E7F4]" : "border-[#E5E7EB] bg-white hover:border-[#965EA7]"}`}>
              <input type="radio" name={q.id} checked={value === o.key} onChange={() => set(q.id, o.key)}
                className="mt-1 h-4 w-4 shrink-0 accent-[#965EA7]" />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  if (q.type === "scale") {
    const steps = [];
    for (let i = q.min; i <= q.max; i += 1) steps.push(i);
    return (
      <fieldset>
        <legend><Label q={q} /></legend>
        <div className="mt-3 flex flex-wrap gap-1.5" role="radiogroup">
          {steps.map((n) => (
            <label key={n} className={`grid h-10 w-10 cursor-pointer place-items-center rounded-xl border text-[15px] font-semibold focus-within:ring-2 focus-within:ring-[#965EA7]
              ${value === n ? "border-[#965EA7] bg-[#965EA7] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]"}`}>
              <input type="radio" name={q.id} checked={value === n} onChange={() => set(q.id, n)} className="sr-only" />
              {n}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  return null;
}
